import { apiClient } from './api'

export interface Product {
  id?: string
  tenant_id?: string
  name: string
  description?: string | null
  sku?: string | null
  hs_code?: string | null
  unit_of_measure?: string | null
  weight?: number | null
  weight_unit?: string | null
  length?: number | null
  width?: number | null
  height?: number | null
  // Local coordinates before rotation, always in mm; null = geometric centre.
  center_of_gravity_x?: number | null
  center_of_gravity_y?: number | null
  center_of_gravity_z?: number | null
  dimension_unit?: string | null
  volume?: number | null
  volume_unit?: string | null
  packaging_type?: string | null
  is_dangerous_goods?: boolean
  dangerous_goods_class?: string | null
  un_number?: string | null
  temperature_controlled?: boolean
  min_temperature?: number | null
  max_temperature?: number | null
  temperature_unit?: string | null
  country_of_origin?: string | null
  status?: string
  created_at?: string
  updated_at?: string
  created_by?: number | null
  updated_by?: number | null
  // Champs d'optimisation 3D
  quantity?: number | null
  stackable?: boolean
  fragile?: boolean
  rotatable?: boolean
  no_stack_above?: boolean
  max_stack_weight?: number | null
  max_stack_layer?: number | null
  color?: string | null
  // Champs avancés d'optimisation
  unloading_order?: number | null
  keep_upright?: boolean
  must_be_on_floor?: boolean
  keep_together_group?: string | null
  min_support_ratio?: number | null
  // Champs de palettisation
  requires_pallet?: boolean
  pallet_type?: string | null
  max_pallet_height_mm?: number | null
  pallet_custom_length?: number | null
  pallet_custom_width?: number | null
  max_items_per_pallet?: number | null
  pallet_board_height_mm?: number | null
  pallet_empty_weight_kg?: number | null
  pallet_color?: string | null
  max_pallet_weight_kg?: number | null
  // Champs de rotation
  allow_rotation_x?: boolean
  allow_rotation_y?: boolean
  allow_rotation_z?: boolean
  rotation_strategy?: string | null
  // Champs de favoris
  favorite_rating?: number | null
}

export interface ProductListResponse {
  products: Product[]
  total: number
  page: number
  pageSize: number
}

export const productService = {
  getProducts: async (params?: { page?: number; pageSize?: number; search?: string }): Promise<ProductListResponse> => {
    const { data } = await apiClient.get<any>('/api/internal/tms/products', {
      params: {
        page: params?.page ?? 1,
        limit: params?.pageSize ?? 20,
        search: params?.search ?? ''
      }
    })

    const products = Array.isArray(data?.data)
      ? data.data
      : Array.isArray(data?.products)
        ? data.products
        : []

    const pagination = data?.pagination || {}

    return {
      products,
      total: pagination.total ?? data?.total ?? products.length,
      page: pagination.page ?? data?.page ?? 1,
      pageSize: pagination.limit ?? data?.pageSize ?? params?.pageSize ?? 20
    }
  },

  getProduct: async (id: string): Promise<Product> => {
    const { data } = await apiClient.get<Product>(`/api/internal/tms/products/${id}`)
    return data
  },

  createProduct: async (product: Omit<Product, 'id' | 'tenant_id' | 'created_at' | 'updated_at'>): Promise<Product> => {
    const { data } = await apiClient.post<Product>('/api/internal/tms/products', product)
    return data
  },

  updateProduct: async (id: string, product: Partial<Product>): Promise<Product> => {
    const { data } = await apiClient.put<Product>(`/api/internal/tms/products/${id}`, product)
    return data
  },

  deleteProduct: async (id: string): Promise<void> => {
    await apiClient.delete(`/api/internal/tms/products/${id}`)
  }
}
